// src/modules/authorized-pickup-persons/hooks/useAuthorizedPickUpPerson.ts
import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
  getAuthorizedPickUpPersons,
  createAuthorizedPickUpPerson,
  updateAuthorizedPickUpPerson,
  deleteAuthorizedPickUpPerson,
  getAuthorizedPickUpPersonById,
  getAuthorizedPickUpPersonsByGuardianId,
  getAuthorizedPickUpPersonsByStudentId,
} from "../../../../services/authorizedPickUpPersonService";
import {
  AuthorizedPickUpPerson,
  AuthorizedPickUpPersonCreatePayload,
  AuthorizedPickUpPersonUpdatePayload,
  AuthorizedPickUpPersonQueryParams,
} from "../../../../types/authorizedPickUpPersonTypes";

export const useAuthorizedPickUpPerson = () => {
  const { t } = useTranslation();

  const [authorizedPickUpPersons, setAuthorizedPickUpPersons] = useState<AuthorizedPickUpPerson[]>([]);
  const [pagination, setPagination] = useState<any>({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedPickUpPerson, setSelectedPickUpPerson] = useState<AuthorizedPickUpPerson | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const relationshipOptions = [
    { label: t("authorizedPickUpPerson.relationship.father"), value: "father" },
    { label: t("authorizedPickUpPerson.relationship.mother"), value: "mother" },
    { label: t("authorizedPickUpPerson.relationship.driver"), value: "driver" },
    { label: t("authorizedPickUpPerson.relationship.brother"), value: "brother" },
    { label: t("authorizedPickUpPerson.relationship.sister"), value: "sister" },
    { label: t("authorizedPickUpPerson.relationship.other"), value: "other" },
    // { label: t("authorizedPickUpPerson.relationship.guardian"), value: "guardian" },
  ];

  const fetchAuthorizedPickUpPersons = async (filters: AuthorizedPickUpPersonQueryParams) => {
    setLoading(true);
    try {
      const response = await getAuthorizedPickUpPersons(filters);
      setAuthorizedPickUpPersons(response.data);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchByGuardianId = async (guardianId: string, filters?: { page?: number; limit?: number }) => {
    setLoading(true);
    try {
      const response = await getAuthorizedPickUpPersonsByGuardianId(guardianId, filters);
      setAuthorizedPickUpPersons(response.data);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchByStudentId = async (studentId: string, filters?: { page?: number; limit?: number }) => {
    setLoading(true);
    try {
      const response = await getAuthorizedPickUpPersonsByStudentId(studentId, filters);
      setAuthorizedPickUpPersons(response.data);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (payload: AuthorizedPickUpPersonCreatePayload) => {
    try {
      await createAuthorizedPickUpPerson(payload);
      message.success(t("authorizedPickUpPerson.success_create"));
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const handleUpdate = async (pickUpPersonId: string, payload: AuthorizedPickUpPersonUpdatePayload) => {
    try {
      await updateAuthorizedPickUpPerson(pickUpPersonId, payload);
      message.success(t("authorizedPickUpPerson.success_update"));
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const handleDelete = async (pickUpPersonId: string) => {
    try {
      await deleteAuthorizedPickUpPerson(pickUpPersonId);
      message.success(t("authorizedPickUpPerson.success_delete"));
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const handleGetById = async (pickUpPersonId: string) => {
    try {
      const response = await getAuthorizedPickUpPersonById(pickUpPersonId);
      return response.data.authorizedPickUpPerson;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const resetSelection = useCallback(() => {
    setSelectedPickUpPerson(null);
    setIsModalOpen(false);
  }, []);

  return {
    authorizedPickUpPersons,
    infoPagination: {
      total: pagination?.total,
      page: pagination?.page,
      limit: pagination?.limit,
    },
    loading,
    error,
    selectedPickUpPerson,
    setSelectedPickUpPerson,
    isModalOpen,
    setIsModalOpen,
    fetchAuthorizedPickUpPersons,
    fetchByGuardianId,
    fetchByStudentId,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleGetById,
    relationshipOptions,
    resetSelection,
  };
};

export default useAuthorizedPickUpPerson;